import { PurcsPreview } from '@vp/react-product-preview';
import { Card, FluidImage } from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { defaultPurcsPreviewOptions } from '../../../apis/purcsApi';
import { SourcePageContext } from '../../contexts/SourcePageContext';

export const ProductImagePreview = ({
  previewMode,
  livePreviewUrl,
  documentRefUrl,
  productAttributeList,
  hasRequiredPurcsData,
  productKey,
  productVersion,
  onClick,
}) => {
  const { sourcePage } = useContext(SourcePageContext);
  const [lineItemMessages] = useTranslations(['lineItemMessages']);

  if (hasRequiredPurcsData) {
    // TODO - capture buyItAgain as part of sourcePage refactor? https://vistaprint.atlassian.net/browse/LOCH-327
    const od = sourcePage === 'orderDetails';
    const oh = sourcePage === 'orderHistory';
    const requestor =
      (od && 'orders.orderDetails') || (oh && 'orders.orderHistory') || '';

    /**
     * Extracts the key and value for each product attribute
     *
     * Before: an array of product attribute objects
     * ex)
     * [
     *  {
     *   key: 'Product Orientation',
     *   value: 'Horizontal',
     *   keyDisplayName: null,
     *   valueDisplayName: null,
     *  },
     * ];
     *
     * After: an object mapping each product attribute key to its value
     * ex)
     * { Product Orientation: 'Horizontal' }
     *
     */
    const optionSelections = productAttributeList.reduce((acc, val) => {
      const { key, value } = val;
      return {
        ...acc,
        [key]: value,
      };
    }, {});

    // TODO: Uncomment and update the following block of code when the PURCS API adds support
    // for fetching the default PURCS preview URL using a doc ref URL (rather than purcs data)
    /**
    // Fetch the defaultView to render a static image if not in Carousel mode (allows centering image within a container)
    if (previewMode !== PreviewModes.Carousel) {
      const defaultPurcsPreviewUrl = getDefaultPurcsPreviewUrl({
        purcsData,
        productKey,
        locale,
        requestor,
      });
      if (defaultPurcsPreviewUrl) {
        return (
          <FluidImage src={defaultPurcsPreviewUrl} alt="" onClick={onClick} />
        );
      }
    }
    */

    return (
      <PurcsPreview
        requestor={requestor}
        coreProductKey={productKey}
        productVersion={productVersion}
        docRefURL={documentRefUrl}
        optionSelections={optionSelections}
        onClickMethod={onClick}
        previewMode={previewMode}
        purcsOrigin={defaultPurcsPreviewOptions.purcsOrigin}
        accessibleTextForNext={
          lineItemMessages.previewProductAccessibleTextForNext
        }
        accessibleTextForPrevious={
          lineItemMessages.previewProductAccessibleTextForPrevious
        }
      />
    );
  }

  if (livePreviewUrl) {
    return (
      <Card backgroundColor="strong" overflow="hidden">
        <FluidImage src={livePreviewUrl} alt="" onClick={onClick} />
      </Card>
    );
  }

  return null;
};

ProductImagePreview.propTypes = {
  previewMode: PropTypes.string,
  livePreviewUrl: PropTypes.string,
  documentRefUrl: PropTypes.string,
  productAttributeList: PropTypes.array,
  hasRequiredPurcsData: PropTypes.bool,
  productKey: PropTypes.string,
  productVersion: PropTypes.number,
  onClick: PropTypes.func,
};
